// @i18n-ignore-file
'use client'
import { useEffect } from 'react'

import { useStatsigDynamicLocaleConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-locale-config.hook'
import { getNonce } from '@src/utils/nonce.utils'

import { useRestoreNextHistoryState } from './use-restore-next-history-state'

const DEFAULT_GLADLY_HELP_CENTER_CDN = 'https://cdn.gladly.com'

// @i18n-ignore
const CONFIG_ID = 'gladly-chat-widget-install'
// @i18n-ignore
const SCRIPT_ID = 'gladly-help-center-sdk'

export const useGladlyHelpCenter = () => {
  const localeConfig = useStatsigDynamicLocaleConfig()

  useRestoreNextHistoryState()

  // Extract stable primitive values to avoid re-running on every render
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- runtime guard for malformed/unknown config payloads
  const helpCenter = localeConfig?.gladlyHelpCenter
  const apiUrl = helpCenter?.apiUrl ?? ''
  const orgId = helpCenter?.orgId ?? ''
  const sdkUrl = helpCenter?.sdkUrl ?? ''
  const brandId = helpCenter?.brandId ?? ''
  const cdn = helpCenter?.cdn ?? DEFAULT_GLADLY_HELP_CENTER_CDN

  useEffect(() => {
    if (typeof window === 'undefined') return

    // Remove any previously appended scripts to avoid duplicates
    document.getElementById(CONFIG_ID)?.remove()
    document.getElementById(SCRIPT_ID)?.remove()

    // Missing config: don't inject broken scripts.
    // (We still removed previous scripts above to avoid stale config.)
    if (!apiUrl || !orgId || !sdkUrl || !brandId) {
      return
    }

    const nonce = getNonce()

    const configScript = document.createElement('script')
    configScript.id = CONFIG_ID
    configScript.innerHTML = ` window.gladlyHCConfig = { api: '${apiUrl}', orgId: '${orgId}', brandId: '${brandId}', cdn: '${cdn}', selector: '#gladly-help-center', autoShowButton: false };` // # nosemgrep

    const sdkScript = document.createElement('script')
    sdkScript.id = SCRIPT_ID
    sdkScript.innerHTML = `function l() { var t = document, e = t.createElement('script'); (e.type = 'text/javascript'), (e.async = !0), (e.src = '${sdkUrl}'); var a = t.getElementsByTagName('script')[0]; a.parentNode.insertBefore(e, a); } var w = window; w.attachEvent ? w.attachEvent('onload', l) : w.addEventListener('DOMContentLoaded', l, !1); l(); ` // # nosemgrep

    if (nonce) {
      configScript.setAttribute('nonce', nonce)
      sdkScript.setAttribute('nonce', nonce)
    }

    document.head.appendChild(configScript)
    document.head.appendChild(sdkScript)

    return () => {
      window.gladlyHCLoaded = false
      document.getElementById(CONFIG_ID)?.remove()
      document.getElementById(SCRIPT_ID)?.remove()
    }
  }, [apiUrl, orgId, sdkUrl, brandId, cdn])
}
